import { request } from '../../services/api';

let razorpayScriptPromise = null;

function loadRazorpayScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window not available'));
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout SDK.'));
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

export function paymentGatewayStatus(message) {
  return message || 'Razorpay gateway ready.';
}

export async function startRazorpayRecharge({ profile, pkg, onStatus, onWalletUpdate }) {
  if (!profile?.mobile) {
    throw new Error('Login required before recharge.');
  }
  onStatus?.({ loading: true, message: 'Checking Razorpay configuration...' });
  const config = await request('/api/payments/razorpay/config');
  if (!config.enabled || !config.keyId) {
    throw new Error('Razorpay is not configured yet. Add your server and client keys in .env first.');
  }

  onStatus?.({ loading: true, message: 'Creating secure Razorpay order...' });
  const orderData = await request('/api/payments/razorpay/order', {
    method: 'POST',
    body: JSON.stringify({ mobile: profile.mobile, packageId: pkg.id }),
  });

  await loadRazorpayScript();

  return await new Promise((resolve, reject) => {
    const options = {
      key: orderData.keyId,
      amount: orderData.order.amount,
      currency: orderData.order.currency,
      name: 'Ludo Pro',
      description: `${pkg.coins} Coins + ${pkg.bonus} Bonus`,
      order_id: orderData.order.id,
      prefill: {
        name: profile.name || '',
        contact: profile.mobile || '',
      },
      notes: {
        mobile: profile.mobile,
        packageId: pkg.id,
      },
      theme: { color: '#6C5CE7' },
      modal: {
        ondismiss: () => {
          onStatus?.({ loading: false, message: 'Payment cancelled. No wallet changes made.' });
          resolve({ status: 'cancelled' });
        },
      },
      handler: async (response) => {
        try {
          onStatus?.({ loading: true, message: 'Verifying payment signature on backend...' });
          const verifyResult = await request('/api/payments/razorpay/verify', {
            method: 'POST',
            body: JSON.stringify({
              mobile: profile.mobile,
              packageId: pkg.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          onWalletUpdate?.(verifyResult, verifyResult.duplicate ? 'Payment already processed once. Wallet kept in sync.' : `${verifyResult.creditedCoins} coins added via Razorpay.`);
          resolve({ status: 'success', payload: verifyResult });
        } catch (error) {
          onStatus?.({ loading: false, message: error.message || 'Payment verification failed.' });
          reject(error);
        }
      },
    };

    const instance = new window.Razorpay(options);
    instance.on('payment.failed', (response) => {
      const reason = response?.error?.description || response?.error?.reason || 'Payment failed.';
      onStatus?.({ loading: false, message: reason });
      resolve({ status: 'failed', reason });
    });
    instance.open();
  });
}
